<template>
  <div
    ref="containerRef"
    :class="[
      'own-segmented-control-2',
      { 'own-segmented-control-2--full-width': fullWidth },
    ]"
  >
    <div
      v-if="indicatorStyle"
      class="own-segmented-control-2__indicator"
      :style="indicatorStyle"
    />
    <button
      v-for="option of options"
      :key="option.value"
      ref="itemRefs"
      :class="[
        'own-segmented-control-2__item px-4',
        { 'own-segmented-control-2__item--full-width': fullWidth },
        { 'own-segmented-control-2__item--active': model === option.value },
      ]"
      @click="selectOption(option.value)"
    >
      <OwnType :text="option.label" variant="button-regular" color="inherit" />
    </button>
  </div>
</template>

<script lang="ts" setup generic="T extends string">
import { useResizeObserver } from '@vueuse/core'
import {
  computed,
  nextTick,
  onMounted,
  ref,
  watch,
  type CSSProperties,
} from 'vue'

import { OwnType } from '../OwnType'
import { Option } from '../types'

const props = withDefaults(
  defineProps<{
    modelValue: T
    options: Array<Option<T>>
    fullWidth?: boolean
  }>(),
  {
    fullWidth: false,
  }
)

const emit = defineEmits<{
  (event: 'update:model-value', value: T): void
}>()

const model = computed({
  get: () => {
    if (props.modelValue) return props.modelValue
    return (props.options[0]?.value ?? 'empty') as T
  },
  set: (value) => {
    if (value === undefined) return
    emit('update:model-value', value)
  },
})

const selectOption = (value: T) => {
  model.value = value
}

const containerRef = ref<HTMLElement | null>(null)
const itemRefs = ref<HTMLElement[]>([])
const indicatorStyle = ref<CSSProperties | null>(null)

const updateIndicator = () => {
  const container = containerRef.value
  if (!container || !itemRefs.value.length) return

  const activeIndex = props.options.findIndex((o) => o.value === model.value)
  if (activeIndex < 0) return

  const activeEl = itemRefs.value[activeIndex]
  if (!activeEl) return

  const containerRect = container.getBoundingClientRect()
  const itemRect = activeEl.getBoundingClientRect()

  indicatorStyle.value = {
    transform: `translateX(${itemRect.left - containerRect.left}px)`,
    width: `${itemRect.width}px`,
  }
}

onMounted(() => {
  nextTick(updateIndicator)
})

useResizeObserver(containerRef, () => updateIndicator())

watch(
  () => props.modelValue,
  () => nextTick(updateIndicator)
)
watch(
  () => props.options,
  () => nextTick(updateIndicator),
  { deep: true }
)
</script>

<style lang="scss" scoped>
.own-segmented-control-2 {
  height: 37px;
  display: inline-flex;
  position: relative;
  background-color: var(--background-secondary);
  border-radius: var(--radius-regular);

  &--full-width {
    width: 100%;
  }

  &__indicator {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    border-radius: var(--radius-regular);
    background: var(--background-primary);
    box-shadow: var(--shadow-card-default);
    transition:
      transform 0.35s cubic-bezier(0.32, 0.72, 0, 1),
      width 0.35s cubic-bezier(0.32, 0.72, 0, 1);
    will-change: transform, width;
    z-index: 0;
  }

  &__item {
    position: relative;
    z-index: 1;
    color: var(--text-color-secondary);
    border-radius: var(--radius-regular);
    cursor: default;
    transition:
      color 0.15s ease-out,
      transform 0.1s cubic-bezier(0.23, 1, 0.32, 1);
    user-select: none;

    &--full-width {
      flex: 1;
      text-align: center;
    }

    &:active {
      transform: scale(0.97);
    }

    &:hover:not(&--active) {
      color: var(--text-color-primary);
    }

    &--active {
      color: var(--text-color-primary);
    }
  }
}
</style>
